import { PromoCampaignService } from '@analytics/feature';
import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { ActiveCartService, CartActions, MultiCartService } from '@cart/data-access';
import { OccCustomerConsignment } from '@checkout/const';
import { CheckoutActions, CheckoutApiService } from '@checkout/data-access';
import { isCheckoutRoute } from '@checkout/utils';
import { CityService } from '@city/feature';
import { EnvironmentService, RunEffectsOnSemanticRouteService } from '@core/feature';
import { Actions, EffectNotification, OnRunEffects, createEffect, ofType } from '@ngrx/effects';
import { CustomOrder } from '@order/const';
import { PaymentService } from '@payment/feature';
import { ShopActions } from '@shop/data-access';
import {
  AuthService,
  OCC_USER_ID_ANONYMOUS,
  RoutingService,
  UserIdService,
  WindowRef,
  normalizeHttpError,
} from '@spartacus/core';
import { CurrentProfileService } from '@user/feature';
import { EMPTY, Observable, combineLatest, from, iif, of } from 'rxjs';
import { catchError, debounceTime, delay, filter, map, switchMap, take, tap } from 'rxjs/operators';
import {
  AdditionalOptionsFormService,
  CheckoutContactFormService,
  CustomCheckoutService,
  PaymentModeService,
  PickupService,
  ReceiptService,
  TimeSlotsService,
} from '../services';

@Injectable()
export class CustomCheckoutEffects implements OnRunEffects {
  // при смене города, корзины или авторизации пользователя перезагружаем чекаут
  handleChangingContextCheckout$: Observable<
    | CheckoutActions.LoadReceiptType
    | ShopActions.LoadShopList
    | CheckoutActions.LoadSavedDeliveryAddressList
    | CheckoutActions.LoadUserPickupShopList
    | CheckoutActions.LoadUserPickupShopListReset
  > = createEffect(() =>
    combineLatest({
      activeBaseStoreCode: this.cityService.activeBaseStoreCode$,
      userId: this.userIdService.getUserId(),
      activeCartId: this.activeCartService.getActiveCartId(),
      routerState: this.routingService.getRouterState(),
    }).pipe(
      filter(({ routerState }) => isCheckoutRoute(routerState.state.semanticRoute)),
      filter(({ activeCartId }) => Boolean(activeCartId)),
      switchMap(({ userId }) => {
        return from([
          new CheckoutActions.LoadReceiptType(),
          new ShopActions.LoadShopList(),
          new CheckoutActions.LoadSavedDeliveryAddressList(),
          userId === OCC_USER_ID_ANONYMOUS
            ? new CheckoutActions.LoadUserPickupShopListReset()
            : new CheckoutActions.LoadUserPickupShopList(),
        ]);
      }),
    ),
  );

  updateCustomerConsignment$: Observable<
    CheckoutActions.UpdateCustomerConsignmentSuccess | CheckoutActions.UpdateCustomerConsignmentFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.UPDATE_CUSTOMER_CONSIGNMENT),
      debounceTime(600),
      map((action: CheckoutActions.UpdateCustomerConsignment) => action.payload),
      switchMap((payload) =>
        combineLatest({
          cartId: this.activeCartService.getActiveCartId(),
        }).pipe(
          take(1),
          filter(({ cartId }) => Boolean(cartId)),
          map(({ cartId }) => ({ cartId, payload })),
        ),
      ),
      switchMap(({ cartId, payload }: { cartId: string; payload: OccCustomerConsignment }) =>
        this.checkoutApiService.setCheckoutData(cartId, payload).pipe(
          map(() => new CheckoutActions.UpdateCustomerConsignmentSuccess(payload)),
          catchError((error) => of(new CheckoutActions.UpdateCustomerConsignmentFail(error))),
        ),
      ),
    ),
  );

  reloadCartOnUpdateCustomerConsignment$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CheckoutActions.UPDATE_CUSTOMER_CONSIGNMENT_SUCCESS),
        switchMap(() =>
          combineLatest({
            userId: this.userIdService.getUserId(),
            cartId: this.activeCartService.getActiveCartId(),
          }).pipe(
            take(1),
            filter(({ cartId, userId }) => !!cartId && !!userId),
          ),
        ),
        map(({ cartId, userId }) =>
          this.multiCartService.loadCart({ cartId: cartId, userId: userId, extraData: { active: true } }),
        ),
      ),
    { dispatch: false },
  );

  resetDeliveryFeatures$: Observable<
    CheckoutActions.TimeSlotsReset | CheckoutActions.ResetOptions | CheckoutActions.SelectActiveDeliveryAddress
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.RESET_DELIVERY_FEATURES),
      switchMap(() =>
        from([
          new CheckoutActions.TimeSlotsReset(),
          new CheckoutActions.ResetOptions(),
          new CheckoutActions.SelectActiveDeliveryAddress(undefined),
        ]),
      ),
    ),
  );

  getPlaceOrderSuccess$: Observable<CheckoutActions.SetOrderDetails> = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.PLACE_ORDER_SUCCESS),
      map((action: CheckoutActions.PlaceOrderSuccess) => action.payload as CustomOrder),
      switchMap((order) =>
        combineLatest({
          order: of(order),
          isCallMe: this.checkoutContactFormService.formValues$.pipe(map((v) => v.callMe)),
          activeReceiptTypeId: this.receiptService.activeReceiptTypeId$,
          isEmpty: this.timeSlotsService.isEmpty(),
          activeShopId: this.pickupService.activeShopId$,
          activePaymentCode: this.paymentModeService.getActivePaymentCode(),
          activePayment: this.authService
            .isUserLoggedIn()
            .pipe(
              switchMap((isUserLoggedIn) =>
                iif(() => isUserLoggedIn, this.paymentService.getActivePayment(), of(null)),
              ),
            ),
        }).pipe(take(1)),
      ),
      map(({ order }) => {
        return { ...order };
      }),
      switchMap((order) => of(new CheckoutActions.SetOrderDetails(order))),
      tap(() => {
        this.checkoutContactFormService.markSuccess();
      }),
    ),
  );

  placeOrder$: Observable<CheckoutActions.PlaceOrderSuccess | CheckoutActions.PlaceOrderFail | CartActions.RemoveCart> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CheckoutActions.PLACE_ORDER),
        map((action: CheckoutActions.PlaceOrder) => action.payload),
        switchMap((payload) => {
          return combineLatest([
            this.currentProfileService
              .get()
              .pipe(map((profile) => (profile.isLegal ? profile.selectedCompanyUid : undefined))),
            this.customCheckoutService.getCustomerConsignment(),
          ]).pipe(
            take(1),
            switchMap(([customerOrganizationUid, customerConsignment]) => {
              const additionalOptions = this.additionalOptionsFormService.getFormValue();

              return this.checkoutApiService
                .placeOrder({
                  cartId: payload.cartId,
                  customerOrganizationUid,
                  termsChecked: payload.termsChecked.toString(),
                  customerConsignment,
                  leaveAtDoor: additionalOptions.leaveByTheDoor,
                  checkoutContactFormData: this.checkoutContactFormService.queryData,
                  promoCampaignParams: this.promoCampaignService.promoCampaignParams,
                })
                .pipe(
                  switchMap((data) => [
                    new CartActions.RemoveCart({ cartId: payload.cartId }) as CartActions.RemoveCart,
                    new CheckoutActions.PlaceOrderSuccess(data) as CheckoutActions.PlaceOrderSuccess,
                  ]),
                  catchError((error) => of(new CheckoutActions.PlaceOrderFail(normalizeHttpError(error)))),
                );
            }),
          );
        }),
      ),
    );

  resetCheckout$: Observable<
    | CheckoutActions.LoadSavedDeliveryAddressListReset
    | CheckoutActions.ResetActiveMode
    | CheckoutActions.TimeSlotsReset
    | CheckoutActions.ResetOptions
    | CheckoutActions.ResetCustomerConsignment
    | CartActions.LoadCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.CHECKOUT_RESET),
      tap(() => {
        this.checkoutContactFormService.reset();
        this.additionalOptionsFormService.reset();
      }),
      switchMap(() =>
        from([
          new CheckoutActions.ResetActiveMode(),
          new CheckoutActions.LoadSavedDeliveryAddressListReset(),
          new CheckoutActions.ResetCustomerConsignment(),
        ]),
      ),
    ),
  );

  resetOrderDetails$: Observable<CheckoutActions.ResetOrderDetails> = createEffect(() =>
    this.actions$.pipe(
      take(1),
      switchMap(() => of(new CheckoutActions.ResetOrderDetails())),
    ),
  );

  createNewCart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CheckoutActions.PLACE_ORDER_SUCCESS),
        switchMap(() => this.userIdService.getUserId()),
        tap((userId) => this.multiCartService.createCart({ userId, extraData: { active: true } })),
      ),
    { dispatch: false },
  );

  confirmOrder$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CheckoutActions.SET_ORDER_DETAILS),
        map(({ payload }) => payload),
        filter((order: CustomOrder) => !!order.code),
        map((order: CustomOrder) => {
          this.customCheckoutService.resetCheckout();

          if (!order.paymentRedirectURL) {
            this.location.replaceState('/cart');
            this.routingService.go(['checkout', 'order-confirmation'].join('/'));

            return;
          }

          this.location.replaceState(['checkout', 'order-confirmation'].join('/'));

          of(true)
            .pipe(delay(1500), take(1))
            .subscribe(() => {
              this.windowRef.nativeWindow.location.href = order.paymentRedirectURL;
            });
        }),
      ),
    { dispatch: false },
  );

  redirectFromCheckoutIfCartIsEmpty$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartActions.LOAD_CART_SUCCESS, CartActions.LOAD_CART_FAIL),
        switchMap((action) => {
          return this.routingService.getRouterState().pipe(
            map((state) => state.state?.semanticRoute),
            filter((semanticRoute) => semanticRoute !== 'login' && semanticRoute !== 'logout'),
            take(1),
            filter((semanticRoute) => isCheckoutRoute(semanticRoute)),
            map(() => action as CartActions.LoadCartSuccess | CartActions.LoadCartFail),
          );
        }),
        map(
          (action) =>
            (action.type === CartActions.LOAD_CART_SUCCESS && action.payload.cart.entries.length === 0) ||
            action.type === CartActions.LOAD_CART_FAIL,
        ),
        filter(Boolean),
        switchMap(() => this.customCheckoutService.waitForRedirectToOrderConfirmationPage$.pipe(take(1))),
        filter((waitForRedirectToOrderConfirmation) => !waitForRedirectToOrderConfirmation),
        map(() => void this.routingService.go({ cxRoute: 'cart' })),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private customCheckoutService: CustomCheckoutService,
    private checkoutContactFormService: CheckoutContactFormService,
    private userIdService: UserIdService,
    private multiCartService: MultiCartService,
    private cityService: CityService,
    private additionalOptionsFormService: AdditionalOptionsFormService,
    private receiptService: ReceiptService,
    private timeSlotsService: TimeSlotsService,
    private pickupService: PickupService,
    private paymentModeService: PaymentModeService,
    private routingService: RoutingService,
    private activeCartService: ActiveCartService,
    private runEffectsOnSemanticRouteService: RunEffectsOnSemanticRouteService,
    private paymentService: PaymentService,
    private authService: AuthService,
    private windowRef: WindowRef,
    private location: Location,
    private environmentService: EnvironmentService,
    private checkoutApiService: CheckoutApiService,
    private promoCampaignService: PromoCampaignService,
    private currentProfileService: CurrentProfileService,
  ) {}

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>): Observable<EffectNotification> {
    return iif(
      () => this.environmentService.isPlatformBrowser,
      this.runEffectsOnSemanticRouteService.ngrxOnRunEffectsOnSemanticRoute(isCheckoutRoute)(resolvedEffects$),
      EMPTY,
    );
  }
}
